/**
 * ===========================================================================
 * CS HUD subsystem  (id: `csui`)
 * ===========================================================================
 *
 * A DOM+CSS overlay in the Counter-Strike idiom: thin, small, high-contrast,
 * monochrome with team accents. It polls the match state owned by the `net`
 * subsystem once per frame and writes only textContent / style / classList.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API — `const cs = ctx.get('csui')`
 * ---------------------------------------------------------------------------
 *   cs.state                 the polled match state (own default until net exists)
 *   cs.overlay.flash(text)   transient toast, e.g. "BUY WINDOW CLOSED"
 *   cs.buy.setOpen(bool)     force the buy menu
 *   cs.scoreboard`           the Tab table
 *   cs.requestLock()         asks the input system to re-take pointer lock
 *
 * Keys, owned here (window, capture phase):
 *   Tab (hold)  scoreboard          — preventDefault only for Tab
 *   B           buy menu            — requires state.inBuyWindow
 *   Esc         close the buy menu
 *   1..9,0      buy the Nth item    — only while the menu is open
 *
 * Closing the buy menu dispatches `ducs:refocus` on window so the input system
 * can re-take pointer lock without either subsystem importing the other.
 *
 * ---------------------------------------------------------------------------
 * READ FROM OTHERS (all optional, all duck-typed)
 * ---------------------------------------------------------------------------
 *   net.state          the object from createMatchState() — the data source
 *   net.buy(key)       purchase request
 *   player.movement.yaw, player.position (or ctx.camera.position) — bearing
 *     for the damage indicator
 */
import { createMatchState } from '../../game/state.js';
import { el, setClass, clamp } from './dom.js';
import { installStyles, removeStyles } from './style.js';
import { RoundBar } from './roundbar.js';
import { BombPanel } from './bomb.js';
import { Vitals, Cash } from './vitals.js';
import { Killfeed } from './killfeed.js';
import { HitMarker } from './hitmarker.js';
import { DamageArc } from './damage.js';
import { Scoreboard } from './scoreboard.js';
import { BuyMenu } from './buymenu.js';
import { Overlay } from './overlay.js';

const DIGITS = {
  Digit1: 0, Digit2: 1, Digit3: 2, Digit4: 3, Digit5: 4,
  Digit6: 5, Digit7: 6, Digit8: 7, Digit9: 8, Digit0: 9,
  Numpad1: 0, Numpad2: 1, Numpad3: 2, Numpad4: 3, Numpad5: 4,
  Numpad6: 5, Numpad7: 6, Numpad8: 7, Numpad9: 8, Numpad0: 9,
};

export class CsHudSystem {
  static id = 'csui';
  static deps = ['ui'];

  async init(ctx) {
    installStyles();

    this.root = document.getElementById('ducs-cs') ?? el('div', null, document.body);
    this.root.id = 'ducs-cs';

    this.round = new RoundBar(this.root);
    this.bomb = new BombPanel(this.root);
    this.vitals = new Vitals(this.root);
    this.cash = new Cash(this.root);
    this.killfeed = new Killfeed(this.root);

    const centre = el('div', 'cs-centre', this.root);
    this.hit = new HitMarker(centre);
    this.dmg = new DamageArc(centre);

    this.scoreboard = new Scoreboard(this.root);
    this.buy = new BuyMenu(this.root);
    this.overlay = new Overlay(this.root);

    this.buy.onBuy = (key) => {
      const net = this._get(this._ctx, 'net');
      if (typeof net?.buy === 'function') net.buy(key);
    };

    /** The state we poll. Replaced by net.state as soon as net exists. */
    this.state = createMatchState();
    this._ctx = ctx ?? null;
    this._tab = false;
    this._sbOpen = false;
    this._k = -1;

    this._onKeyDown = (e) => this._keydown(e);
    this._onKeyUp = (e) => this._keyup(e);
    this._onBlur = () => {
      this._tab = false;
    };
    window.addEventListener('keydown', this._onKeyDown, true);
    window.addEventListener('keyup', this._onKeyUp, true);
    window.addEventListener('blur', this._onBlur);

    const h = ctx?.canvas?.clientHeight || window.innerHeight || 1080;
    const w = ctx?.canvas?.clientWidth || window.innerWidth || 1920;
    this.resize(w, h, ctx);
  }

  /* ------------------------------------------------------------ plumbing -- */

  /** ctx.get() throws for unregistered subsystems; peek at it defensively. */
  _get(ctx, id) {
    if (!ctx || typeof ctx.get !== 'function') return null;
    try {
      return ctx.get(id) ?? null;
    } catch {
      return null;
    }
  }

  _state(ctx) {
    const net = this._get(ctx, 'net');
    const s = net?.state ?? this.state;
    this.state = s;
    return s;
  }

  /** Ask the input system to re-take pointer lock after a menu closed. */
  requestLock() {
    window.dispatchEvent(new CustomEvent('ducs:refocus'));
  }

  _openBuy() {
    if (this.buy.open) return;
    this.buy.setOpen(true);
  }

  _closeBuy() {
    if (!this.buy.open) return;
    this.buy.setOpen(false);
    window.dispatchEvent(new CustomEvent('ducs:refocus'));
  }

  _keydown(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;

    if (e.code === 'Tab' || e.key === 'Tab') {
      // Only Tab is ever swallowed, and only while the HUD is live.
      this._tab = true;
      e.preventDefault();
      return;
    }

    if (this.buy.open) {
      if (e.code === 'Escape' || e.code === 'KeyB') {
        this._closeBuy();
        e.preventDefault();
        return;
      }
      const d = DIGITS[e.code];
      if (d !== undefined) {
        this.buy.key(d);
        e.preventDefault();
      }
      return;
    }

    if (e.code === 'KeyB') {
      e.preventDefault();
      if (this.state?.inBuyWindow) this._openBuy();
      else this.overlay.flash('BUY WINDOW CLOSED');
    }
  }

  _keyup(e) {
    if (e.code === 'Tab' || e.key === 'Tab') this._tab = false;
  }

  /* --------------------------------------------------------------- frame -- */

  update(dt, ctx) {
    if (!this.root) return;
    this._ctx = ctx ?? this._ctx;
    const s = this._state(ctx);
    const now = performance.now() * 0.001;

    this.round.update(s);
    this.bomb.update(s, now);
    this.vitals.update(s, now);
    this.cash.update(s);
    this.killfeed.update(s?.killfeed, now);

    this.hit.update(s?.hitmarker, dt);

    const player = this._get(ctx, 'player');
    const yaw = player?.movement?.yaw ?? 0;
    let px = 0;
    let pz = 0;
    const pos = player?.position ?? ctx?.camera?.position ?? null;
    if (pos) {
      px = pos.x ?? 0;
      pz = pos.z ?? 0;
    }
    this.dmg.update(s?.damageFlash, dt, px, pz, yaw);

    // ---- scoreboard ------------------------------------------------------
    const wantSb = this._tab || !!s?.showScoreboard;
    if (wantSb !== this._sbOpen) {
      this._sbOpen = wantSb;
      setClass(this.scoreboard.root, 'on', wantSb);
    }
    if (wantSb) this.scoreboard.update(s);

    // ---- buy menu --------------------------------------------------------
    if (this.buy.open && !s?.inBuyWindow) this._closeBuy();
    if (this.buy.open) this.buy.update(s);

    this.overlay.update(s, dt);
  }

  resize(w, h, ctx) {
    if (!this.root) return;
    const k = clamp((h || 1080) / 1080, 0.62, 2.4);
    if (k === this._k) return;
    this._k = k;
    this.root.style.setProperty('--k', k.toFixed(4));
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown, true);
    window.removeEventListener('keyup', this._onKeyUp, true);
    window.removeEventListener('blur', this._onBlur);
    this._onKeyDown = null;
    this._onKeyUp = null;
    this._onBlur = null;
    this.buy?.dispose();
    this.root?.remove();
    this.root = null;
    removeStyles();
  }
}
